import { Formik } from 'formik';
import * as React from 'react';
import * as Yup from 'yup';
import { useKioskState } from '../../../context/Kiosk/kiosk';
import { CTAButton } from '../../../controls/ButtonBase';
import { useLocalization, useLocalizationExtension } from '../../../context/Localization/localization';
import { Heading } from '../../../controls/Heading/Heading';
import { VersionInfo } from '../../../controls/VersionInfo/VersionInfo';
import { KeyboardLayouts } from '../../../controls/SimpleKeyboard/Layout';
import { useAdminLogin, useAdminState, useRequestMFACode } from '../../../context/Admin/admin';
import { default as css } from './IdentityVerification.module.scss';
import { nameof } from '../../../helpers/nameof';
import { GMFormPasswordField } from '../../../controls/TextField';
import { GMDialogError } from '../../../controls/Dialog';
import { GMForm, GMFormWrapper } from '../../../controls/Form';

interface IProps {
    onProceed: () => void;
}

export const IdentityVerification = (props: IProps) => {
    const Localization = useLocalization();
    const { formatInvalid } = useLocalizationExtension();
    const initialValues: { securityCode?: string; } = {};
    const {
        key: kioskKey,
        dealer: { key: dealerKey },
    } = useKioskState();
    const [securityCodeInvalid, setSecurityCodeInvalid] = React.useState(false);
    const [resultInvalid, setResultInvalid] = React.useState(false);
    const [error, setError] = React.useState<string | undefined>(undefined);
    const validate = useAdminLogin();
    const requestMFACode = useRequestMFACode();
    const adminState = useAdminState();
    const [codeSentMessage, setCodeSentMessage] = React.useState(false);

    /*
     * TODO: This if statement causes a bad setState warning. We skip past this while states are setting up
     * and we should just check before we get here if we should skip.
     */
    if (adminState.LoginSessionID !== undefined) {
        props.onProceed();

        return <></>;
    }

    return (
        <>
            <VersionInfo />
            <Formik
                initialValues={initialValues}
                validate={async (values) => {
                    if (securityCodeInvalid) {
                        if (values.securityCode && values.securityCode.length !== 6) {
                            return { [nameof<typeof initialValues>('securityCode')]: Localization.SecurityCodeDigits };
                        }

                        if (resultInvalid) {
                            setResultInvalid(false);

                            return { [nameof<typeof initialValues>('securityCode')]: Localization.SecurityCodeIncorrect };
                        }

                        return { [nameof<typeof initialValues>('securityCode')]: formatInvalid(Localization.SecurityCode) };
                    }
                    else {
                        return await Yup.object()
                            .shape({
                                [nameof<typeof initialValues>('securityCode')]: Yup.number()
                                    .required(formatInvalid(Localization.SecurityCode))
                                    .typeError(formatInvalid(Localization.SecurityCode)),
                            })
                            .validate(values)
                            .then(
                                () => ({}),
                                (error) => ({ [error.path]: error.message }),
                            );
                    }
                }}
                onSubmit={async (values, { setFieldError }) => {
                    if (!values.securityCode) {
                        setSecurityCodeInvalid(true);
                        setFieldError(nameof<typeof initialValues>('securityCode'), formatInvalid(Localization.SecurityCode));

                        return;
                    }

                    if (values.securityCode.length !== 6) {
                        setSecurityCodeInvalid(true);
                        setFieldError(nameof<typeof initialValues>('securityCode'), Localization.SecurityCodeDigits);

                        return;
                    }

                    const result = await validate({
                        kioskKey,
                        dealerKey,
                        pin: adminState.PIN ?? '',
                        code: values.securityCode,
                    });
                    if (result.error) {
                        setError(result.message);
                        setSecurityCodeInvalid(true);
                        setResultInvalid(true);
                        setFieldError(nameof<typeof initialValues>('securityCode'), Localization.SecurityCodeIncorrect);
                    }
                    else {
                        props.onProceed();
                    }
                }}
            >
                <GMForm
                    heading={
                        <Heading
                            primaryHeading={Localization.IdentityVerification}
                            secondaryHeading={
                                <div style={{
                                    fontSize: '28px',
                                    textAlign: 'left',
                                }}
                                >
                                    <div style={{ marginBottom: '10px' }}>{Localization.MultifactorExplaination1}</div>
                                    <div>{Localization.MultifactorExplaination2}</div>
                                </div>
                            }
                        />
                    }
                    submitButton={
                        <CTAButton
                            styleType={'primary'}
                            type={'submit'}
                            label={Localization.Verify}
                            onClick={() => setCodeSentMessage(false)}
                            id={Localization.Verify}
                            ariaLabel={Localization.Verify}
                        />
                    }
                >
                    <GMFormWrapper finePrint requiredFieldLabel>{Localization.RequiredFields}</GMFormWrapper>
                    <div
                        style={{
                            marginTop: '55px',
                            marginBottom: '40px',
                            height: '170px',
                            marginLeft: 'auto',
                            marginRight: 'auto',
                            width: 'max-content',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            textAlign: 'center',
                        }}
                    >
                        {adminState.PhoneNumberMFADisplay && (
                            <CTAButton
                                type={'button'}
                                style={{ margin: '5px' }}
                                onClick={async (e) => {
                                    e.preventDefault();

                                    setCodeSentMessage(false);

                                    const result = await requestMFACode({
                                        kioskKey,
                                        dealerKey,
                                        pin: adminState.PIN ?? '',
                                        deliveryMethod: 'phone',
                                    });

                                    if (result.error) {
                                        setError(result.message);
                                    }
                                    else {
                                        setCodeSentMessage(true);
                                    }
                                }}
                                styleType={'primary'}
                                label={adminState.PhoneNumberMFADisplay}
                                id={adminState.PhoneNumberMFADisplay}
                                ariaLabel={adminState.PhoneNumberMFADisplay}
                            />
                        )}
                        {adminState.EmailAddressMFADisplay && (
                            <CTAButton
                                type={'button'}
                                style={{ margin: '5px' }}
                                onClick={async (e) => {
                                    e.preventDefault();

                                    setCodeSentMessage(false);

                                    const result = await requestMFACode({
                                        kioskKey,
                                        dealerKey,
                                        pin: adminState.PIN || '',
                                        deliveryMethod: 'email',
                                    });

                                    if (result.error) {
                                        setError(result.message);
                                    }
                                    else {
                                        setCodeSentMessage(true);
                                    }
                                }}
                                styleType={'primary'}
                                label={adminState.EmailAddressMFADisplay}
                                id={adminState.EmailAddressMFADisplay}
                                ariaLabel={adminState.EmailAddressMFADisplay}
                            />
                        )}
                        {codeSentMessage && <div style={{
                            fontSize: '28px',
                            alignSelf: 'center',
                        }}
                        >{Localization.CodeSent}
                        </div>}
                    </div>
                    <GMFormPasswordField
                        name={nameof<typeof initialValues>('securityCode')}
                        id={nameof<typeof initialValues>('securityCode')}
                        title={Localization.SecurityCode}
                        label={Localization.SecurityCode}
                        ariaLabel={Localization.SecurityCode}
                        keyboardLayout={KeyboardLayouts.NumberPad}
                        onChangeCallback={() => setSecurityCodeInvalid(false)}
                        autoFocus
                        required
                    />

                    {!adminState.PhoneNumberMFADisplay && !adminState.EmailAddressMFADisplay && (
                        <div className={css.errorMessage}>{Localization.AddEmailPhoneToDashboard}</div>
                    )}
                </GMForm>
            </Formik>
            <GMDialogError
                ariaLabel={Localization.AnErrorHasOccured}
                open={!!error}
                onClose={() => setError(undefined)}
            >
                {error}
            </GMDialogError>
        </>
    );
};
